import React, { useCallback, useEffect, useState } from 'react';
import { Building2, ClipboardList, Inbox, Mail, MapPin, Phone } from 'lucide-react';
import { adminApi } from '../../services/api';
import { EtatErreur } from '../../etats';
import { Carte, Chargement, fmtDate, Pastille, TexteLignes, Vide } from './ui';

function Ligne({ libelle, valeur }) {
  if (!valeur && valeur !== 0) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3 py-2 border-b border-[var(--border)] last:border-0">
      <dt className="sm:w-44 shrink-0 text-xs font-semibold text-[var(--text-muted)]">{libelle}</dt>
      <dd className="text-sm text-[var(--text-bold)] break-words">{valeur}</dd>
    </div>
  );
}

function etatContrat(contrat) {
  if (!contrat) return 'Pas encore émis';
  if (contrat.valide_par_admin) return 'Validé';
  return contrat.signe_par_org ? 'Signé, en attente de validation' : 'En attente de signature';
}

/* Ce que l'association a transmis en s'inscrivant, reuni pour le coach. */
export default function DossierProjet({ projetId }) {
  const [dossier, setDossier] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [essai, setEssai] = useState(0);
  const reessayer = useCallback(() => setEssai((n) => n + 1), []);

  useEffect(() => {
    let vivant = true;
    setErreur(null);
    adminApi.getDossierProjet(projetId)
      .then((d) => vivant && setDossier(d))
      .catch((e) => vivant && setErreur(e));
    return () => { vivant = false; };
  }, [projetId, essai]);

  // L'objet erreur, pas son texte : son statut (403, 404…) choisit l'écran.
  if (erreur && !dossier) return <EtatErreur erreur={erreur} onReessayer={reessayer} />;
  if (!dossier) return <Chargement forme="cartes" />;

  const { organisation: org, projet, demandes, contacts_expert: contacts, diagnostic_public: diag } = dossier;
  const docs = dossier.documents || {};

  return (
    <div className="grid lg:grid-cols-2 gap-4 items-start">
      <Carte titre="Association">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-pastille bg-[var(--bg-main)] flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-[var(--accent-texte)]" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-[var(--text-bold)]">{org.nom}</p>
            <p className="text-xs text-[var(--text-muted)]">{[org.type, org.budget_annuel].filter(Boolean).join(' · ')}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--text-main)] mb-3">
          {(org.ville || org.pays) && (
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{[org.ville, org.pays].filter(Boolean).join(', ')}</span>
          )}
          {org.contact && (
            <a href={`tel:${org.contact}`} className="inline-flex items-center gap-1.5 hover:text-[var(--accent-texte)]"><Phone className="w-3.5 h-3.5" />{org.contact}</a>
          )}
          {org.email && (
            <a href={`mailto:${org.email}`} className="inline-flex items-center gap-1.5 hover:text-[var(--accent-texte)] break-all"><Mail className="w-3.5 h-3.5" />{org.email}</a>
          )}
        </div>
        <dl>
          <Ligne libelle="Inscrite le" valeur={fmtDate(org.date_creation)} />
          <Ligne libelle="Accompagnement" valeur={`${projet.type} · ${projet.statut}`} />
          <Ligne libelle="Demande reçue le" valeur={fmtDate(projet.date_demande)} />
          <Ligne libelle="Fin prévue" valeur={projet.date_fin_prevue ? fmtDate(projet.date_fin_prevue) : ''} />
          <Ligne libelle="Participants" valeur={`${dossier.participants.dirigeants} dirigeant(s), ${dossier.participants.membres} membre(s)`} />
          <Ligne libelle="Documents" valeur={`${docs.soumis || 0} à valider, ${docs.valide || 0} validé(s), ${docs.rejete || 0} rejeté(s)`} />
          <Ligne libelle="Contrat" valeur={etatContrat(dossier.contrat)} />
        </dl>
        {projet.message_demande && (
          <div className="mt-4 bg-[var(--bg-main)] rounded-pastille p-4">
            <p className="surtitre text-[var(--text-muted)] mb-1">Message de l'association</p>
            <TexteLignes texte={projet.message_demande} className="text-sm text-[var(--text-main)]" />
          </div>
        )}
      </Carte>

      <div className="space-y-4">
        <Carte titre="Diagnostic public">
          {!diag ? (
            <Vide icone={ClipboardList} titre="Aucun diagnostic public terminé"
              texte="L'association n'a pas passé le diagnostic en ligne sous ce nom d'organisation." />
          ) : (
            <div>
              <div className="flex flex-wrap items-baseline gap-x-3 mb-3">
                <p className="text-3xl font-semibold text-[var(--text-bold)] tabular-nums">{Math.round(diag.score_total || 0)} %</p>
                <p className="text-sm text-[var(--text-muted)]">
                  {[diag.niveau, diag.categorie && `catégorie ${diag.categorie}`, fmtDate(diag.date)].filter(Boolean).join(' · ')}
                </p>
              </div>
              {diag.scores_domaines && (
                <ul className="space-y-2 mb-3">
                  {Object.entries(diag.scores_domaines).map(([nom, score]) => (
                    <li key={nom}>
                      <div className="flex justify-between gap-3 text-xs mb-1">
                        <span className="text-[var(--text-main)]">{nom}</span>
                        <span className="font-semibold tabular-nums">{Math.round(score)} %</span>
                      </div>
                      <div className="h-1.5 rounded-controle bg-[var(--bg-main)] overflow-hidden">
                        <div className="h-full bg-[var(--accent)]" style={{ width: `${Math.min(100, score)}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {(diag.faiblesses || []).length > 0 && (
                <div>
                  <p className="surtitre text-[var(--text-muted)] mb-1">Faiblesses relevées</p>
                  <ul className="list-disc pl-5 text-sm text-[var(--text-main)] space-y-1">
                    {diag.faiblesses.map((f, i) => (
                      <li key={i}><span className="font-semibold">{f.sous_domaine}</span> — {f.description}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Carte>

        <Carte titre="Demandes et prises de contact">
          {demandes.length === 0 && contacts.length === 0 ? (
            <Vide icone={Inbox} titre="Aucune demande retrouvée"
              texte="Les demandes sont rapprochées par projet, par adresse e-mail ou par nom d'organisation." />
          ) : (
            <ul className="space-y-3">
              {demandes.map((d) => (
                <li key={d.id} className="bg-[var(--bg-main)] rounded-pastille p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Pastille className="text-[var(--accent-texte)] bg-[var(--accent)]/10 border-[var(--accent)]/30">{d.type}</Pastille>
                    <span className="text-xs text-[var(--text-muted)]">{fmtDate(d.date)} · {d.nom} · {d.telephone || d.email}</span>
                  </div>
                  {d.modules_souhaites && <p className="text-xs text-[var(--text-muted)] mb-1">Modules souhaités : {d.modules_souhaites}</p>}
                  <TexteLignes texte={d.message} className="text-sm text-[var(--text-main)]" />
                </li>
              ))}
              {contacts.map((c, i) => (
                <li key={`contact-${i}`} className="bg-[var(--bg-main)] rounded-pastille p-4">
                  <p className="text-xs text-[var(--text-muted)] mb-1">Demande de contact expert · {fmtDate(c.date)} · {c.contact}</p>
                  <TexteLignes texte={c.commentaire || '(sans commentaire)'} className="text-sm text-[var(--text-main)]" />
                </li>
              ))}
            </ul>
          )}
        </Carte>
      </div>
    </div>
  );
}